/**
 * 租户模块配置
 * 统一管理租户模块的路由、菜单、权限配置
 * @author AI Assistant
 * @date 2024-07-08
 */

import type { ModuleConfig } from '@/types/module-config'

/**
 * 租户模块配置
 */
const moduleConfig: ModuleConfig = {
  id: 'tenant',
  name: '租户管理',
  description: '租户管理模块，包含租户列表、租户配置等功能',
  version: '1.0.0',
  enabled: true,

  // 路由配置
  routes: [
    {
      path: '/tenant',
      name: 'Tenant',
      component: '@/layouts/index.vue',
      redirect: '/tenant/list',
      meta: {
        title: '租户管理',
        icon: 'OfficeBuilding',
        roles: ['admin', 'super_admin'],
        alwaysShow: true,
        order: 90
      },
      children: [
        {
          path: 'list',
          name: 'TenantList',
          component: '@/modules/tenant/pages/list.vue',
          meta: {
            title: '租户列表',
            icon: 'List',
            permissions: ['tenant:list:view'],
            keepAlive: true
          }
        },
        {
          path: 'apps',
          name: 'TenantApps',
          component: '@/modules/tenant/pages/apps.vue',
          meta: {
            title: '租户应用',
            icon: 'Grid',
            permissions: ['tenant:apps:view'],
            keepAlive: true
          }
        },
        {
          path: 'orders',
          name: 'TenantOrders',
          component: '@/modules/tenant/pages/orders.vue',
          meta: {
            title: '订单管理',
            icon: 'Document',
            permissions: ['tenant:orders:view'],
            keepAlive: true
          }
        }
      ]
    }
  ],

  // 权限配置
  permissions: [
    {
      code: 'tenant:list:view',
      name: '查看租户列表',
      type: 'menu',
      description: '查看租户列表',
      apiPath: '/tenants'
    },
    {
      code: 'tenant:list:add',
      name: '添加租户',
      type: 'button',
      description: '添加新租户',
      apiPath: '/tenants'
    },
    {
      code: 'tenant:list:edit',
      name: '编辑租户',
      type: 'button',
      description: '编辑租户信息',
      apiPath: '/tenants/:id'
    },
    {
      code: 'tenant:list:delete',
      name: '删除租户',
      type: 'button',
      description: '删除租户',
      apiPath: '/tenants/:id'
    },
    {
      code: 'tenant:list:detail',
      name: '查看租户详情',
      type: 'operation',
      description: '查看租户详细信息',
      apiPath: '/tenants/:id'
    },
    {
      code: 'tenant:list:status',
      name: '修改租户状态',
      type: 'operation',
      description: '启用/禁用租户',
      apiPath: '/tenants/:id/status'
    },
    {
      code: 'tenant:apps:view',
      name: '查看租户应用',
      type: 'menu',
      description: '查看租户应用列表',
      apiPath: '/tenant-apps'
    },
    {
      code: 'tenant:apps:manage',
      name: '管理租户应用',
      type: 'button',
      description: '管理租户应用',
      apiPath: '/tenant-apps/:id'
    },
    {
      code: 'tenant:apps:config',
      name: '配置租户应用',
      type: 'operation',
      description: '配置租户应用参数',
      apiPath: '/tenant-apps/:id/config'
    },
    {
      code: 'tenant:orders:view',
      name: '查看订单管理',
      type: 'menu',
      description: '查看订单列表',
      apiPath: '/orders'
    },
    {
      code: 'tenant:orders:manage',
      name: '管理订单',
      type: 'button',
      description: '管理订单信息',
      apiPath: '/orders/:id'
    },
    {
      code: 'tenant:orders:detail',
      name: '查看订单详情',
      type: 'operation',
      description: '查看订单详细信息',
      apiPath: '/orders/:id'
    },
    {
      code: 'tenant:orders:status',
      name: '修改订单状态',
      type: 'operation',
      description: '修改订单状态',
      apiPath: '/orders/:id/status'
    }
  ],

  // 导航配置
  navigation: {
    name: 'tenant',
    title: '租户管理',
    icon: 'OfficeBuilding',
    order: 90,
    permissions: ['tenant:view'],
    items: [
      {
        id: 'tenant-list',
        title: '租户列表',
        path: '/tenant/list',
        icon: 'List',
        order: 1,
        permissions: ['tenant:list:view'],
        module: 'tenant',
        component: '@/modules/tenant/pages/list.vue'
      },
      {
        id: 'tenant-apps',
        title: '租户应用',
        path: '/tenant/apps',
        icon: 'Grid',
        order: 2,
        permissions: ['tenant:apps:view'],
        module: 'tenant',
        component: '@/modules/tenant/pages/apps.vue'
      },
      {
        id: 'tenant-orders',
        title: '订单管理',
        path: '/tenant/orders',
        icon: 'Document',
        order: 3,
        permissions: ['tenant:orders:view'],
        module: 'tenant',
        component: '@/modules/tenant/pages/orders.vue'
      }
    ]
  }
}

export default moduleConfig
